import { Routes } from '@angular/router';
import { AppLayout } from '@/layout/components/app.layout';
import { AuthGuard } from '@/guards/auth.guard';

export const appRoutes: Routes = [
    // Redirect root to login
    { path: '', redirectTo: '/login', pathMatch: 'full' },

    // Login route (no layout)
    { path: 'login', loadComponent: () => import('@/pages/auth/mpi-login').then(c => c.MpiLogin), title: "Login" },
    { path: 'register', loadComponent: () => import('@/pages/auth/register/register').then(c => c.Register), title: "Register" },
    { path: 'forgotpassword', loadComponent: () => import('@/pages/auth/forgotpassword').then(c => c.ForgotPassword), title: "Forgot Password" },
    { path: 'reset-password', loadComponent: () => import('@/pages/auth/newpassword').then(c => c.NewPassword), title: "Reset Password" },
    { path: 'new-account', loadComponent: () => import('@/pages/new-account/new-account.component').then(c => c.NewAccountComponent), title: "New Rental Account" },
    { path: 'verify-account', loadComponent: () => import('@/pages/auth/email-verification').then(c => c.EmailVerification), title: "Verify Account" },
    { path: 'accept-invitation', loadComponent: () => import('@/pages/accept-invitation/accept-invitation.component').then(c => c.AcceptInvitationComponent), title: 'Accept Invitation' },
    { path: 'logout', loadComponent: () => import('@/pages/auth/logout').then(c => c.Logout) },
    { path: 'unsubscribe', loadComponent: () => import('@/pages/unsubscribe/unsubscribe.component').then(c => c.UnsubscribeComponent), title: 'Unsubscribe from Email Notifications' },
    // Protected route for linking additional tenant accounts (used after login redirect)
    { path: 'link-account', loadComponent: () => import('@/pages/link-account/link-account.component').then(c => c.LinkAccountComponent), canActivate: [AuthGuard], title: 'Link Rental Account' },
    { path: 'change-password/:token', loadComponent: () => import('@/pages/change-password/change-password.component').then(c => c.ChangePasswordComponent) },
    {
        path: '',
        component: AppLayout,
        children: [
            {
                path: 'tenant',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_TENANT' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@/pages/dashboards/tenant-dashboard/tenant-dashboard').then(c => c.TenantDashboard),
                        title: "Tenant Dashboard"
                    }, {
                        path: 'dashboard',
                        loadComponent: () => import('@/pages/dashboards/tenant-dashboard/tenant-dashboard').then(c => c.TenantDashboard),
                        title: "Tenant Dashboard"
                    }, {
                        path: 'manage-payment-methods',
                        loadComponent: () => import('@/pages/manage-payment-methods/manage-payment-methods').then(c => c.ManagePaymentMethods),
                        title: "Manage Payment Methods"
                    }, {
                        path: 'autopay',
                        loadComponent: () => import('@/pages/manage-autopay/manage-autopay').then(c => c.ManageAutopay),
                        title: "Autopay Settings"
                    }, {
                        path: 'invoice-list',
                        loadComponent: () => import('@/pages/tenant-invoices/tenant-invoices-list').then(c => c.TenantInvoiceList),
                        title: "Invoice List"
                    }, {
                        path: 'lease-documents',
                        loadComponent: () => import('@/pages/tenant-lease-documents/tenant-lease-documents').then(c => c.TenantLeaseDocuments),
                        title: "Lease Documents"
                    }, {
                        path: 'email-notifications',
                        loadComponent: () => import('@/pages/tenant-email-notifications/tenant-email-notifications').then(c => c.TenantEmailNotificationsComponent),
                        title: "Email Notifications"
                    }, {
                        path: 'payment-transactions',
                        loadComponent: () => import('@/pages/tenant-payment-transactions/tenant-payment-transactions.component').then(c => c.TenantPaymentTransactionsComponent),
                        title: "Payment Transactions"
                    }, {
                        path: 'link-account',
                        loadComponent: () => import('@/pages/link-account/link-account.component').then(c => c.LinkAccountComponent),
                        title: "Link Another Rental Account"
                    }
                ]
            },
            {
                path: 'admin',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_ADMIN' },
                children: [
                    {
                        path: '',
                        loadComponent: () => import('@/pages/dashboards/admin-dashboard/admin-dashboard').then(c => c.AdminDashboard),
                        canActivate: [AuthGuard],
                        title: "Admin Dashboard"
                    },
                    {
                        path: 'dashboard',
                        loadComponent: () => import('@/pages/dashboards/admin-dashboard/admin-dashboard').then(c => c.AdminDashboard),
                        canActivate: [AuthGuard],
                        title: "Admin Dashboard"
                    }, {
                        path: 'settings',
                        loadComponent: () => import('@/pages/settings/settings.component').then(c => c.SettingsComponent),
                        canActivate: [AuthGuard],
                        title: "Settings"
                    },
                    {
                        path: 'property-settings',
                        loadComponent: () => import('@/pages/property-settings/property-settings.component').then(c => c.PropertySettingsComponent),
                        canActivate: [AuthGuard],
                        title: "Property Settings"
                    },
                    {
                        path: 'monthly-billing',
                        loadComponent: () => import('@/pages/monthly-billing/monthly-billing').then(c => c.MonthlyBilling),
                        canActivate: [AuthGuard],
                        title: "Monthly Billing"
                    },
                    {
                        path: 'manage-tenants',
                        loadComponent: () => import('@/pages/manage-tenants/manage-tenants').then(c => c.ManageTenants),
                        canActivate: [AuthGuard],
                        title: "Manage Tenants"
                    },
                    {
                        path: 'pending-invitations',
                        loadComponent: () => import('@/pages/pending-invitations/pending-invitations.component').then(c => c.PendingInvitationsComponent),
                        canActivate: [AuthGuard],
                        title: "Pending Invitations"
                    },
                    {
                        path: 'manage-logins',
                        loadComponent: () => import('@/pages/manage-logins/manage-logins').then(c => c.ManageLogins),
                        canActivate: [AuthGuard],
                        title: "Manage User Logins"
                    },
                    {
                        path: 'manage-leases',
                        loadComponent: () => import('@/pages/leases/manage-leases').then(c => c.ManageLeases),
                        canActivate: [AuthGuard],
                        title: "Manage Leases"
                    },
                    {
                        path: 'manage-suites',
                        loadComponent: () => import('@/pages/suites/manage-suites').then(c => c.ManageSuites),
                        canActivate: [AuthGuard],
                        title: "Manage Suites"
                    },
                    {
                        path: 'invoice-list',
                        loadComponent: () => import('@/pages/invoice-list/invoice-list').then(c => c.InvoiceList),
                        canActivate: [AuthGuard],
                        title: "Invoice List"
                    },
                    {
                        path: 'invoice-billing-report',
                        loadComponent: () => import('@/pages/invoice-billing-report/invoice-billing-report').then(c => c.InvoiceBillingReportComponent),
                        canActivate: [AuthGuard],
                        title: "Invoice Billing Report"
                    },
                    {
                        path: 'monthly-billing-history',
                        loadComponent: () => import('@/pages/monthly-billing-history/monthly-billing-history').then(c => c.MonthlyBillingHistoryComponent),
                        canActivate: [AuthGuard],
                        title: "Monthly Billing History"
                    },
                    {
                        path: 'account-requests',
                        loadComponent: () => import('@/pages/account-requests/account-requests.component').then(c => c.AccountRequestsComponent),
                        canActivate: [AuthGuard],
                        title: "Account Requests"
                    },
                    {
                        path: 'payment-logs',
                        loadComponent: () => import('@/pages/payment-logs/payment-logs.component').then(c => c.PaymentLogsComponent),
                        canActivate: [AuthGuard],
                        title: "Payment Logs"
                    },
                    {
                        path: 'bank-deposit',
                        loadComponent: () => import('@/pages/bank-deposit/bank-deposit.component').then(c => c.BankDepositComponent),
                        canActivate: [AuthGuard],
                        title: "Bank Deposit"
                    },
                    {
                        path: 'pending-payments-report',
                        loadComponent: () => import('@/pages/pending-payments-report/pending-payments-report').then(c => c.PendingPaymentsReportComponent),
                        canActivate: [AuthGuard],
                        title: "Pending Payments Report"
                    },
                    {
                        path: 'tenant-activity',
                        loadComponent: () => import('@/pages/tenant-activity/tenant-activity').then(c => c.TenantActivity),
                        canActivate: [AuthGuard],
                        title: "Tenant Activity"
                    },
                    {
                        path: 'charge-codes',
                        loadComponent: () => import('@/pages/charge-codes/charge-codes').then(c => c.ChargeCodes),
                        canActivate: [AuthGuard],
                        title: "Charge Code Management"
                    },
                    {
                        path: 'receive-payment',
                        loadComponent: () => import('@/pages/receive-payment/receive-payment').then(c => c.ReceivePayment),
                        title: "Receive Payment"
                    },
                    {
                        path: 'invoice-tenant',
                        loadComponent: () => import('@/pages/invoice-tenant/invoice-tenant').then(c => c.InvoiceTenant),
                        canActivate: [AuthGuard],
                        title: "Invoice Tenant"
                    },
                    {
                        path: 'email-logs',
                        loadComponent: () => import('@/pages/email-logs/email-logs.component').then(c => c.EmailLogsComponent),
                        canActivate: [AuthGuard],
                        title: "Email Logs"
                    },
                    {
                        path: 'system-status',
                        loadComponent: () => import('@/pages/admin/system-status/system-status.component').then(c => c.SystemStatusComponent),
                        canActivate: [AuthGuard],
                        title: "System Status"
                    },
                    {
                        path: 'link-tenant-to-ach-deposit-account',
                        loadComponent: () => import('@/pages/link-tenant-to-ach-deposit-account/link-tenant-to-ach-deposit-account.component').then(c => c.LinkTenantToAchDepositAccountComponent),
                        canActivate: [AuthGuard],
                        title: "Link Tenant To ACH Deposit Account"
                    },
                    {
                        path: 'ach-deposit-reconciliation',
                        loadComponent: () => import('@/pages/ach-deposit-reconciliation/ach-deposit-reconciliation.component').then(c => c.AchDepositReconciliationComponent),
                        canActivate: [AuthGuard],
                        title: "ACH Deposit Reconciliation"
                    },
                    {
                        path: 'autopay-logs',
                        loadComponent: () => import('@/pages/autopay-logs/autopay-logs').then(c => c.AutopayLogs),
                        canActivate: [AuthGuard],
                        title: "AutoPay Logs"
                    },
                    {
                        path: 'aged-receivables-report',
                        loadComponent: () => import('@/pages/aged-receivables-report/aged-receivables-report').then(c => c.AgedReceivablesReport),
                        canActivate: [AuthGuard],
                        title: "Aged Receivables Report"
                    },
                    {
                        path: 'dev/accept-blue-customers',
                        loadComponent: () => import('@/pages/dev/accept-blue-customers/accept-blue-customers').then(c => c.AcceptBlueCustomers),
                        canActivate: [AuthGuard],
                        title: "Accept Blue Customers"
                    },
                    {
                        path: 'dev/accept-blue-customers/:id',
                        loadComponent: () => import('@/pages/dev/accept-blue-customer-detail/accept-blue-customer-detail').then(c => c.AcceptBlueCustomerDetail),
                        canActivate: [AuthGuard],
                        title: "Accept Blue Customer Detail"
                    },
                    {
                        path: 'dev/aged-receivables',
                        loadComponent: () => import('@/pages/dev/aged-receivables/aged-receivables').then(c => c.AgedReceivables),
                        canActivate: [AuthGuard],
                        title: "Aged Receivables"
                    },
                    {
                        path: 'dev/medici-ledger',
                        loadComponent: () => import('@/pages/dev/medici-ledger/medici-ledger').then(c => c.MediciLedger),
                        canActivate: [AuthGuard],
                        title: "Medici Ledger"
                    },
                    {
                        path: 'dev/ledger',
                        loadComponent: () => import('@/pages/dev/invoice-ledger/invoice-ledger').then(c => c.InvoiceLedger),
                        canActivate: [AuthGuard],
                        title: "Invoice Ledger"
                    },
                    {
                        path: 'dev/credit-balance-reconciliation',
                        loadComponent: () => import('@/pages/dev/credit-balance-reconciliation/credit-balance-reconciliation').then(c => c.CreditBalanceReconciliation),
                        canActivate: [AuthGuard],
                        title: "Credit Balance Reconciliation"
                    },
                    {
                        path: 'dev/settings',
                        loadComponent: () => import('@/pages/dev/developer-settings/developer-settings').then(c => c.DeveloperSettings),
                        canActivate: [AuthGuard],
                        title: "Developer Settings"
                    },
                    {
                        path: 'dev/monthly-billing',
                        loadComponent: () => import('@/pages/dev/monthly-billing-preview/monthly-billing-preview').then(c => c.MonthlyBillingPreview),
                        canActivate: [AuthGuard],
                        title: "Billing Preview"
                    },
                    {
                        path: 'dev/rate-bump-preview',
                        loadComponent: () => import('@/pages/dev/rate-bump-preview/rate-bump-preview').then(c => c.RateBumpPreview),
                        canActivate: [AuthGuard],
                        title: "Rate Bump Preview"
                    },
                ]
            },
            {
                path: 'accounting',
                canActivate: [AuthGuard],
                data: { role: 'ROLE_ACCOUNTING' },
                children: [{
                    path: '',
                    loadComponent: () => import('@/pages/dashboards/accounting-dashboard/accounting-dashboard').then(c => c.AccountingDashboard),
                    title: "Accounting Dashboard"
                }, {
                    path: 'dashboard',
                    loadComponent: () => import('@/pages/dashboards/accounting-dashboard/accounting-dashboard').then(c => c.AccountingDashboard),
                    title: "Accounting Dashboard"
                }, {
                    path: 'accounting-activity',
                    loadComponent: () => import('@/pages/accounting-activity/accounting-activity').then(c => c.AccountingActivity),
                    title: "Accounting Activity"
                }, {
                    path: 'charge-codes',
                    loadComponent: () => import('@/pages/charge-codes/charge-codes').then(c => c.ChargeCodes),
                    title: "Charge Code Management"
                }, {
                    path: 'invoice-tenant',
                    loadComponent: () => import('@/pages/invoice-tenant/invoice-tenant').then(c => c.InvoiceTenant),
                    title: "Invoice Tenant"
                }, {
                    path: 'receive-payment',
                    loadComponent: () => import('@/pages/receive-payment/receive-payment').then(c => c.ReceivePayment),
                    title: "Receive Payment"
                }, {
                    path: 'invoice-list',
                    loadComponent: () => import('@/pages/invoice-list/invoice-list').then(c => c.InvoiceList),
                    canActivate: [AuthGuard],
                    title: "Invoice List"
                }, {
                    path: 'email-logs',
                    loadComponent: () => import('@/pages/email-logs/email-logs.component').then(c => c.EmailLogsComponent),
                    canActivate: [AuthGuard],
                    title: "Email Logs"
                }, {
                    path: 'ach-deposit-reconciliation',
                    loadComponent: () => import('@/pages/ach-deposit-reconciliation/ach-deposit-reconciliation.component').then(c => c.AchDepositReconciliationComponent),
                    canActivate: [AuthGuard],
                    title: "ACH Deposit Reconciliation"
                }, {
                    path: 'link-tenant-to-ach-deposit-account',
                    loadComponent: () => import('@/pages/link-tenant-to-ach-deposit-account/link-tenant-to-ach-deposit-account.component').then(c => c.LinkTenantToAchDepositAccountComponent),
                    canActivate: [AuthGuard],
                    title: "Link Tenant To ACH Deposit Account"
                },]
            },
            {
                path: 'dev/view-invoice',
                loadComponent: () => import('@/pages/dev/view-invoice').then(c => c.ViewInvoice),
                canActivate: [AuthGuard]
            },
            {
                path: 'account-settings',
                loadComponent: () => import('@/pages/account-settings/account-settings.component').then(c => c.AccountSettingsComponent),
                canActivate: [AuthGuard],
                title: "Account Settings"
            }
        ]
    },
    // Auth routes
    { path: 'auth', loadChildren: () => import('@/pages/auth/auth.routes') },
    { path: 'landing', loadComponent: () => import('@/pages/landing/landing').then(c => c.Landing) },
    { path: 'notfound', loadComponent: () => import('@/pages/notfound/notfound').then(c => c.Notfound) },
    { path: '**', redirectTo: '/notfound' }
];
